import type { SetupMacroStepId, SetupSourcePath } from '@/components/setup/experience/types';

export interface SetupMacroStepDefinition {
  id: SetupMacroStepId;
  labelKey: string;
}

export const SETUP_MACRO_STEPS: SetupMacroStepDefinition[] = [
  { id: 'store', labelKey: 'experience.steps.store' },
  { id: 'products', labelKey: 'experience.steps.products' },
  { id: 'shipping', labelKey: 'experience.steps.shipping' },
  { id: 'payment', labelKey: 'experience.steps.payment' },
  { id: 'checkout', labelKey: 'experience.steps.checkout' },
];

export interface SetupSourceOptionDefinition {
  id: SetupSourcePath;
  nameKey: string;
  hintKey: string;
  popular?: boolean;
}

export const SETUP_PATH_OPTIONS: SetupSourceOptionDefinition[] = [
  {
    id: 'shopify',
    nameKey: 'experience.sources.shopify.name',
    hintKey: 'experience.sources.shopify.hint',
    popular: true,
  },
  {
    id: 'woocommerce',
    nameKey: 'experience.sources.woocommerce.name',
    hintKey: 'experience.sources.woocommerce.hint',
  },
  {
    id: 'manual',
    nameKey: 'experience.sources.manual.name',
    hintKey: 'experience.sources.manual.hint',
  },
];

export type SetupPaymentProcessorType = 'sandbox' | 'tagadapay-router';

export const SETUP_PAYMENT_PROCESSOR_TYPES: SetupPaymentProcessorType[] = ['sandbox', 'tagadapay-router'];

export function getRecommendedPaymentProcessor(
  isTestEnv: boolean,
  hasLivePayment: boolean,
): SetupPaymentProcessorType {
  return !isTestEnv && hasLivePayment ? 'tagadapay-router' : 'sandbox';
}

export const SETUP_MOTION = {
  enter: (dir: number) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -48 : 48, opacity: 0 }),
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
} as const;

export const SETUP_PATH_MOTION = {
  intro: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.44, ease: [0.22, 1, 0.36, 1] as const },
  },
  list: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.12 },
    },
  },
  option: {
    hidden: { opacity: 0, y: 14 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
    },
  },
  actions: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const, delay: 0.36 },
  },
  footer: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.44, ease: [0.22, 1, 0.36, 1] as const, delay: 0.44 },
  },
  selected: {
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 420, damping: 28 },
  },
  radioDot: {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0, opacity: 0 },
    transition: { type: 'spring' as const, stiffness: 520, damping: 26 },
  },
} as const;

/** Shared input styling for the connect-step forms (Shopify, WooCommerce). */
export const SETUP_FIELD_INPUT_CLASS =
  '!h-11 !min-h-11 !border !border-border !rounded-lg !bg-background !px-3 !py-0 !text-sm !leading-tight !font-normal !shadow-none transition-[border-color,box-shadow,background-color] duration-150 ease-[ease] placeholder:text-[color-mix(in_srgb,var(--muted-foreground)_65%,transparent)] hover:enabled:aria-[invalid=false]:!border-[color-mix(in_srgb,var(--primary)_22%,var(--border))] focus-visible:!border-primary focus-visible:!outline-none focus-visible:!shadow-[0_0_0_2px_color-mix(in_srgb,var(--primary)_14%,transparent)] aria-[invalid=true]:!border-destructive aria-[invalid=true]:!shadow-[0_0_0_2px_color-mix(in_srgb,var(--destructive)_12%,transparent)]';
